import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useAppStore, useToasts } from '../store/useAppStore';
import type { Toast as ToastItem } from '../types';

/**
 * アプリ共通のトースト表示。
 *
 * これが無かったため、App.tsx 各所の addToast(...) は 1 件も画面に出ていなかった
 * （DB保存失敗・QR読み取り失敗・写真アップロード失敗などが全て無言で消えていた）。
 * store に溜まるだけで描画されていなかったので、ここでまとめて出す。
 *
 * 置き場所は index.tsx（App の外）。App.tsx は view ごとに早期 return するため、
 * App の中に置くと表示される画面とされない画面が出てしまう。
 */

const STYLES: Record<ToastItem['type'], { box: string; icon: React.ReactNode }> = {
  success: {
    box: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    icon: <CheckCircle2 size={18} className="text-emerald-600" />,
  },
  error: {
    box: 'bg-red-50 border-red-300 text-red-900',
    icon: <XCircle size={18} className="text-red-600" />,
  },
  warning: {
    box: 'bg-amber-50 border-amber-300 text-amber-900',
    icon: <AlertTriangle size={18} className="text-amber-600" />,
  },
  info: {
    box: 'bg-slate-50 border-slate-300 text-slate-800',
    icon: <Info size={18} className="text-slate-500" />,
  },
};

const ToastRow: React.FC<{ toast: ToastItem; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const { duration = 5000 } = toast;

  useEffect(() => {
    // duration <= 0 は「自動で消さない」の意味にする
    if (duration <= 0) return;
    const t = window.setTimeout(() => onClose(toast.id), duration);
    return () => window.clearTimeout(t);
  }, [toast.id, duration, onClose]);

  const style = STYLES[toast.type] ?? STYLES.info;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto flex items-start gap-2 w-full border rounded-xl shadow-lg px-3.5 py-3 ${style.box}`}
    >
      <span className="shrink-0 mt-0.5">{style.icon}</span>
      <p className="flex-1 text-sm leading-snug break-words">{toast.message}</p>
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        aria-label="通知を閉じる"
        className="shrink-0 -mr-1 -mt-1 p-1 opacity-50 hover:opacity-100 transition"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const toasts = useToasts();
  const removeToast = useAppStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    // スマホでは画面上部（親指の届く位置より上・ヘッダーの下）、デスクトップでは右上。
    <div className="pointer-events-none fixed inset-x-3 top-3 z-[100] flex flex-col gap-2 md:inset-x-auto md:right-4 md:top-4 md:w-96">
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} onClose={removeToast} />
      ))}
    </div>
  );
};

export default ToastContainer;
